/**
 * DATCopyHelper — Click-to-copy-Panel mit den wichtigsten Akten-Daten,
 * die in DAT calculateExpert gebraucht werden. Liegt direkt über dem
 * Iframe und ist klappbar, damit der User bei DAT-SSO/Auto-Fill-Problemen
 * Werte schnell per 1-Klick in die Zwischenablage holen und manuell in
 * die DAT-Felder einfügen kann.
 *
 * Props:
 *   case      Volle Akte (case-Objekt aus Backend).
 *   defaultOpen?  Initial sichtbar (default: false — eingeklappt)
 */
import React, { useState } from "react";
import { Copy, ClipboardList, ChevronDown, ChevronUp, CheckCircle2, User, Car, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DATCopyHelper({ case: c, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(null);

  if (!c) return null;
  const cust = c.customer || {};
  const veh = c.vehicle || {};
  const ins = c.insurance || {};

  const copy = async (value, key) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(key);
      toast.success("Kopiert", { description: String(value).slice(0, 60) });
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  // Format helpers
  const fmtPlate = (v) => (v || "").toUpperCase();
  const fmtVIN = (v) => (v || "").toUpperCase();
  const fmtKBA = () => {
    const h = (veh.hsn || "").trim();
    const t = (veh.tsn || "").trim();
    if (h && t) return `${h}${t}`;
    return veh.kba || "";
  };
  const fmtDate = (v) => {
    if (!v) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
  };
  const fmtMileage = (v) => {
    if (!v) return "";
    const n = parseInt(v, 10);
    return isFinite(n) ? n.toLocaleString("de-DE") : v;
  };
  const fmtName = () => `${cust.firstname || ""} ${cust.name || ""}`.trim();
  const fmtAddress = () => {
    const street = cust.address || "";
    const plzCity = `${cust.postal_code || ""} ${cust.city || ""}`.trim();
    return street && plzCity ? `${street}, ${plzCity}` : (street || plzCity);
  };

  // Build the 4 groups with [label, value, key, format-hint]
  const groups = [
    { title: "Halter", icon: User, items: [
      ["Name", fmtName(), "name"],
      ["Adresse", fmtAddress(), "address"],
      ["Telefon", cust.phone, "phone"],
      ["E-Mail", cust.email, "email"],
    ] },
    { title: "Fahrzeug", icon: Car, items: [
      ["Kennzeichen", fmtPlate(veh.license_plate), "plate"],
      ["VIN", fmtVIN(veh.vin), "vin"],
      ["KBA / HSN+TSN", fmtKBA(), "kba"],
      ["DAT-€-Code", veh.dat_eur_code, "datcode"],
      ["Erstzulassung", fmtDate(veh.first_registration), "first_reg"],
      ["KM-Stand", fmtMileage(veh.mileage), "mileage"],
      ["Marke / Modell", `${veh.make || ""} ${veh.model || ""}`.trim(), "make_model"],
    ] },
    { title: "Versicherung", icon: Shield, items: [
      ["Versicherer", ins.insurer_name, "insurer"],
      ["Police-Nr.", ins.policy_number, "policy"],
      ["Schaden-Nr.", ins.claim_number, "claim_num"],
      ["Schadendatum", fmtDate(ins.claim_date), "claim_date"],
      ["Schadenort", ins.damage_location, "claim_loc"],
    ] },
    { title: "Gegner", icon: AlertTriangle, items: [
      ["Gegner-Name", cust.opponent_name, "opp_name"],
      ["Gegner-Kennz.", fmtPlate(cust.opponent_plate), "opp_plate"],
      ["Gegner-Vers.", cust.opponent_insurance, "opp_ins"],
    ] },
  ];

  // Filter groups that have no values at all
  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter(([, v]) => v) }))
    .filter((g) => g.items.length > 0);

  if (visibleGroups.length === 0) return null;

  return (
    <div className="border-b border-cyan-100 bg-cyan-50/30" data-testid="dat-copy-helper">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-2.5 flex items-center gap-3 hover:bg-cyan-50/60 transition-colors text-left"
        data-testid="dat-copy-helper-toggle"
      >
        <ClipboardList className="h-4 w-4 text-cyan-700" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-700">
            Eckdaten zum Kopieren
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            Falls DAT-Felder leer bleiben — Klick auf Feld kopiert den Wert in die Zwischenablage.
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="px-5 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dat-copy-helper-grid">
          {visibleGroups.map((g) => (
            <div key={g.title} className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-cyan-800 pb-1 border-b border-cyan-200">
                <g.icon className="h-3 w-3" />
                {g.title}
              </div>
              {g.items.map(([label, value, key]) => (
                <CopyChip
                  key={key}
                  label={label}
                  value={value}
                  isCopied={copied === key}
                  onCopy={() => copy(value, key)}
                  testid={`copy-${key}`}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CopyChip({ label, value, isCopied, onCopy, testid }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      title={`„${value}" kopieren`}
      data-testid={testid}
      className={[
        "w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors group",
        "border border-transparent hover:border-cyan-300 hover:bg-white",
        isCopied ? "bg-emerald-50 border-emerald-300" : "bg-white/60",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-500 leading-tight">{label}</div>
          <div className="text-[12px] text-zinc-900 font-medium break-words leading-tight">{value || "—"}</div>
        </div>
        {isCopied ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-zinc-400 group-hover:text-cyan-600 shrink-0" />
        )}
      </div>
    </button>
  );
}
